/**
 * The Transfer Portal tab.
 *
 * A tab of its own beside Sources rather than a mode inside Add Source, and
 * that is the point rather than a layout preference: a Transfer Portal Profile
 * has no Feed, no Binding, no Stat Dictionary scope, no ranking method and no
 * Credential Set, so an operator sent to Add Source for one would be shown a
 * form that is mostly fields which do not apply.
 *
 * **Nothing here names a publisher.** The menu and the form are rendered from
 * the declarations the server hands over, through the same `TemplateParameters`
 * component the source menu uses — so 247Sports' `sportKey` and its `yearSport`
 * are never something a person is asked for, and adding On3 costs no change in
 * this file. The two composed addresses *are* shown, deliberately: they are
 * displayed, never built here, and an operator about to save a profile is
 * entitled to see what it will fetch.
 *
 * What the screen decides rather than shows lives in `../transfer-portal`,
 * because `npm test` here is bare `node --test` with no framework and anything
 * worth asserting has to be free of React.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  api,
  templateDefaults,
  type TemplateValues,
  type TransferPortalProfile,
} from "../api";
import { POLL_LIST_MS, keys } from "../queries";
import { Layout } from "../components/Layout";
import { TemplateParameters } from "../components/TemplateParameters";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Field,
  KeyValue,
  Muted,
  Select,
  Spinner,
  StatusBadge,
  TableScroll,
  TextInput,
} from "../components/ui";
import { DownloadIcon, PlayIcon, PlusIcon, TrashIcon } from "../components/icons";
import { formatRelative, formatTimestamp } from "../format";
import {
  lastRunVerdict,
  nextFiring,
  runDownloadUrl,
  transferPortalCreateBody,
  transferPortalCredentialNote,
  transferPortalNamePlaceholder,
} from "../transfer-portal";

export function TransferPortal() {
  const [adding, setAdding] = useState(false);

  return (
    <Layout
      title="Transfer Portal"
      description={
        <>
          Players currently in a publisher's transfer portal, looked up against the
          ranked file this portal already published for that League and Season. Nothing
          here is ranked — every modelled number is carried across from the ranking Run
          that produced it.
        </>
      }
      actions={
        <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setAdding(true)}>
          Add a transfer portal
        </Button>
      }
    >
      {adding && <AddTransferPortal onDone={() => setAdding(false)} />}
      <ProfileList onAdd={() => setAdding(true)} />
    </Layout>
  );
}

// --- The list ---------------------------------------------------------------

function ProfileList({ onAdd }: { onAdd: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const { data: profiles, error } = useQuery({
    queryKey: keys.transferPortalProfiles,
    queryFn: api.listTransferPortalProfiles,
    // A Run in flight changes this screen without anyone touching it.
    refetchInterval: POLL_LIST_MS,
  });

  const startRun = useMutation({
    mutationFn: (profile: TransferPortalProfile) => api.startTransferPortalRun(profile.id),
    // Straight to the Run: the phase and the counts are the content there, and
    // this list is about to be stale anyway.
    onSuccess: (run) => navigate(`/runs/${run.id}`),
    onError: (failure: unknown) =>
      setNotice(failure instanceof ApiError ? failure.message : "That Run could not be started."),
  });

  const remove = useMutation({
    mutationFn: (profile: TransferPortalProfile) => api.deleteTransferPortalProfile(profile.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.transferPortalProfiles }),
  });

  if (error) {
    return (
      <Banner tone="danger" title="The transfer portal list could not be loaded">
        {error instanceof ApiError ? error.message : "Something went wrong."}
      </Banner>
    );
  }
  if (!profiles) return <Spinner label="Loading transfer portals" />;

  if (profiles.length === 0) {
    return (
      <EmptyState
        title="No transfer portals yet"
        action={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={onAdd}>
            Add a transfer portal
          </Button>
        }
      >
        A transfer portal profile looks players up in the ranked file for its League and
        Season, so run that League's Source Profile first — otherwise the first Run will
        tell you the file it needs is not there yet.
      </EmptyState>
    );
  }

  return (
    <>
      {notice && (
        <Banner tone="warning" title="That Run was not started" className="mb-md">
          {notice}
        </Banner>
      )}
      <Card flush>
        <TableScroll stickyHeader>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Publisher</th>
                <th>League</th>
                <th>Season</th>
                <th>Last Run</th>
                <th>Outcome</th>
                <th>Next Run</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <ProfileRow
                  key={profile.id}
                  profile={profile}
                  onRun={() => startRun.mutate(profile)}
                  onDelete={() => remove.mutate(profile)}
                  busy={startRun.isPending || remove.isPending}
                />
              ))}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </>
  );
}

/**
 * What the list says about when this profile runs next.
 *
 * Three states, because a dash for all of them would hide the one that needs
 * attention: a profile nobody put on a timer is working as intended, and a
 * cron saved but switched off is not.
 */
function NextRunCell({ profile }: { profile: TransferPortalProfile }) {
  const firing = nextFiring(profile);
  if (firing.kind === "scheduled") {
    return <span title={formatTimestamp(firing.at)}>{formatRelative(firing.at)}</span>;
  }
  if (firing.kind === "configured") {
    return <Muted>Scheduled, switched off</Muted>;
  }
  return <Muted>Manual</Muted>;
}


function ProfileRow({
  profile,
  onRun,
  onDelete,
  busy,
}: {
  profile: TransferPortalProfile;
  onRun: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const run = profile.last_run;
  const verdict = lastRunVerdict(run);
  // Only a Run that published has a file to hand over, and it names it itself.
  const download = run && run.status === "succeeded" ? profile : null;

  return (
    <tr>
      <td className="font-text text-body-sm-medium">{profile.name}</td>
      <td>{profile.publisher}</td>
      <td>{profile.league}</td>
      <td>{profile.season}</td>
      <td>{run ? formatRelative(run.created_at) : <Muted>Never</Muted>}</td>
      <td>
        <span className="flex items-center gap-xs">
          {verdict.status && <StatusBadge status={verdict.status} />}
          {run ? (
            <Link to={`/runs/${run.id}`} className="text-body-sm">
              {verdict.label}
            </Link>
          ) : (
            <Muted>{verdict.label}</Muted>
          )}
        </span>
      </td>
      <td>
        <NextRunCell profile={profile} />
      </td>
      <td>
        <span className="flex justify-end gap-xs">
          {download && <DownloadLink profile={profile} />}
          <Button size="sm" icon={<PlayIcon size={14} />} onClick={onRun} disabled={busy}>
            Run
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={<TrashIcon size={14} />}
            onClick={onDelete}
            disabled={busy}
            title={`Delete ${profile.name}`}
          >
            Delete
          </Button>
        </span>
      </td>
    </tr>
  );
}

/**
 * The file the last successful Run published.
 *
 * Fetched from the Run rather than from object storage, so checking a published
 * sheet needs no AWS console — and so that a destination repointed since the Run
 * says so rather than answering an unexplained 404.
 */
function DownloadLink({ profile }: { profile: TransferPortalProfile }) {
  const run = profile.last_run;
  const { data } = useQuery({
    queryKey: keys.run(run?.id ?? ""),
    queryFn: () => api.readRun(run!.id),
    enabled: Boolean(run),
  });
  const filename = data?.available_downloads[0];
  if (!run || !filename) return null;

  return (
    <a className="btn btn-secondary btn-sm" href={runDownloadUrl(run.id, filename)} download>
      <DownloadIcon size={14} />
      CSV
    </a>
  );
}

// --- Adding one -------------------------------------------------------------

function AddTransferPortal({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [values, setValues] = useState<TemplateValues | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const { data: templates } = useQuery({
    queryKey: keys.transferPortalTemplates,
    queryFn: api.listTransferPortalTemplates,
  });

  // One publisher today and a menu tomorrow; the form is written for the menu,
  // so adding On3 is a declaration on the server and nothing here.
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const template = useMemo(
    () => templates?.find((t) => t.key === (templateKey ?? templates[0]?.key)) ?? null,
    [templates, templateKey],
  );
  const parameters = values ?? (template ? templateDefaults(template) : null);

  const { data: composition, error: composeError } = useQuery({
    queryKey: keys.transferPortalComposition(template?.key ?? "", parameters),
    queryFn: () => api.composeTransferPortalTemplate(template!.key, parameters!),
    enabled: Boolean(template && parameters),
  });

  const create = useMutation({
    mutationFn: () => api.createTransferPortalProfile(transferPortalCreateBody(composition!, name)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.transferPortalProfiles });
      onDone();
    },
    onError: (failure: unknown) =>
      setError(failure instanceof ApiError ? failure : null),
  });

  if (!templates) return <Spinner label="Loading the transfer portal menu" />;
  if (!template || !parameters) return null;

  return (
    <Card
      title="Add a transfer portal"
      description={template.description}
      className="mb-lg"
      actions={
        <>
          <Button onClick={onDone}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={!composition || !name.trim() || create.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      {error && (
        <Banner tone="danger" title="That transfer portal was not saved" className="mb-md">
          {error.message}
        </Banner>
      )}
      {composeError instanceof ApiError && (
        <Banner tone="warning" title="These settings compose nothing" className="mb-md">
          {composeError.message}
        </Banner>
      )}

      <Field label="Name" hint="What this profile is called in the list above.">
        <TextInput
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={transferPortalNamePlaceholder(
            template.publisher,
            template.parameters,
            parameters,
          )}
        />
      </Field>

      {templates.length > 1 && (
        <Field label="Publisher" className="mt-md">
          <Select
            value={template.key}
            onChange={(event) => {
              setTemplateKey(event.target.value);
              setValues(null);
            }}
          >
            {templates.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <TemplateParameters
        template={template}
        values={parameters}
        onChange={setValues}
        invalid={error?.field}
        message={error?.message}
      />

      {composition && <ComposedSummary defaults={composition.defaults} />}
    </Card>
  );
}

/**
 * What these settings compose, shown before anything is saved.
 *
 * The League above all: it decides both which ranked file the Run reads and
 * what the published file is called, and it is derived from the sport rather
 * than typed. Showing it is what makes "the sport decides the League" something
 * a person sees happen rather than something a docstring claims.
 */
function ComposedSummary({ defaults }: { defaults: Record<string, unknown> }) {
  const text = (key: string) => String(defaults[key] ?? "—");
  return (
    <div className="mt-lg rounded-lg border border-hairline bg-surface-card p-md">
      <div className="grid gap-sm sm:grid-cols-2">
        <KeyValue label="League">{text("league")}</KeyValue>
        <KeyValue label="Sport">{text("sport")}</KeyValue>
        <KeyValue label="Transfer list">
          <span className="break-all">{text("transfers_url")}</span>
        </KeyValue>
        {/* Only where there is one. A publisher that needs no Source Session
            composes no page, and a row reading "—" invites an operator to go
            looking for an address that does not exist. */}
        {defaults.page_url ? (
          <KeyValue label="Guest token page">
            <span className="break-all">{text("page_url")}</span>
          </KeyValue>
        ) : null}
        {/* The floor a fuzzy name match has to clear. Shown because it is the
            number that decides whether a wrong row reaches the file, and an
            operator reading a falling match rate has to be able to see what it
            is set to. */}
        <KeyValue label="Name match threshold">{text("name_match_threshold")}%</KeyValue>
      </div>
      <Muted className="mt-sm block">{transferPortalCredentialNote(defaults)}</Muted>
    </div>
  );
}
